/**
 * Video Exporter - WebCodecs + mp4-muxer for fast video export
 *
 * Refactored to use modular architecture:
 * - lib/export-config.js - Centralized configuration
 * - lib/encoder-setup.js - Encoder creation and codec selection
 * - lib/frame-renderer.js - Frame rendering loop
 * - lib/lyrics-compositor.js - Lyrics compositing
 */

import { VisualizerRegistry } from './visualizer.js';
import { AudioAnalyzer } from './audio-analyzer.js';

// Import refactored modules
import {
  VIDEO_CONFIG,
  AUDIO_CONFIG,
  TIMEOUT_CONFIG,
  ANALYSIS_CONFIG,
  DEFAULT_EXPORT_OPTIONS,
  getPlatformCleanupDelay,
} from './lib/export-config.js';
import {
  selectVideoCodec,
  createVideoEncoder,
  createAudioEncoder,
  createMuxer,
  assembleChunksToBlob,
  audioBufferToAudioData,
  loadMuxer,
  detectSoftwareFallback,
} from './lib/encoder-setup.js';
import { FrameRenderer, buildTrackTimeline } from './lib/frame-renderer.js';
import { createLyricsCompositor } from './lib/lyrics-compositor.js';
import { ResourceRegistry, ResourceAuditor } from './lib/resource-registry.js';

/**
 * Resolve the audio source for a playlist track.
 * Handles both local files (item.file) and pre-downloaded streamed tracks (item.rawAudioData).
 */
function resolveTrackAudioSource(item) {
  if (item.rawAudioData) return item.rawAudioData;
  if (item.file) return item.file;
  throw new Error(`Track "${item.name || 'unknown'}" has no audio source`);
}

class VideoExporter {
  constructor(options = {}) {
    // Merge with defaults
    const opts = { ...DEFAULT_EXPORT_OPTIONS, ...options };

    this.width = opts.width;
    this.height = opts.height;
    this.fps = opts.fps;
    this.videoBitrate = opts.videoBitrate;
    this.videoCodecPreset = opts.videoCodecPreset;
    this.audioBitrate = opts.audioBitrate;
    this.visualizerType = opts.visualizerType;
    this.visualizerConfig = opts.visualizerConfig;
    this.analysisFftSize = opts.analysisFftSize;
    this.analysisOverlapSamples = opts.analysisOverlapSamples || opts.analysisFftSize;

    // Callbacks
    this.onProgress = opts.onProgress || null;
    this.onTrackChange = opts.onTrackChange || null;
    this.onBytesWritten = opts.onBytesWritten || null;
    this.onStatsUpdate = opts.onStatsUpdate || null;

    // Lyrics options
    this.lyricsRenderer = opts.lyricsRenderer || null;
    this.lyricsEnabled = opts.lyricsEnabled !== false;

    // State
    this.abortRequested = false;
    this.muxerLoaded = false;
    this.writableStream = null;
    this.totalBytesWritten = 0;
  }

  /**
   * Check if File System Access API is supported
   */
  supportsFileSystemAccess() {
    return 'showSaveFilePicker' in window;
  }

  /**
   * Request abort of current export
   */
  abort() {
    this.abortRequested = true;
  }

  /**
   * Prompt user for save location using File System Access API
   * @param {string} suggestedName - Suggested filename
   * @returns {Promise<{stream: FileSystemWritableFileStream, fileName: string}>}
   */
  async promptForSaveLocation(suggestedName) {
    const fileHandle = await window.showSaveFilePicker({
      suggestedName,
      types: [{
        description: 'MP4 Video',
        accept: { 'video/mp4': ['.mp4'] }
      }]
    });
    const stream = await fileHandle.createWritable();
    return { stream, fileName: fileHandle.name };
  }

  /**
   * Load mp4-muxer from CDN
   */
  async loadMuxer() {
    if (this.muxerLoaded) return;
    await loadMuxer(TIMEOUT_CONFIG.cdnLoad);
    this.muxerLoaded = true;
  }

  /**
   * Initialize visualizer for export
   * @param {OffscreenCanvas} canvas - Target canvas
   * @returns {Promise<Object>} Visualizer instance
   */
  async initVisualizer(canvas) {
    // Get visualizer class from registry
    const VisualizerClass = await VisualizerRegistry.load(this.visualizerType);
    if (!VisualizerClass) {
      throw new Error(`Unknown visualizer type: ${this.visualizerType}`);
    }

    // Create instance in export mode
    const visualizer = new VisualizerClass(canvas, {
      ...this.visualizerConfig,
      exportMode: true
    });

    // Wait for async initialization (important for WebGPU)
    if (visualizer.ready === false) {
      console.log('[Export] Waiting for visualizer to initialize...');
      let waitTime = 0;
      while (!visualizer.ready && waitTime < TIMEOUT_CONFIG.visualizerInit) {
        await new Promise(resolve => setTimeout(resolve, TIMEOUT_CONFIG.visualizerPollInterval));
        waitTime += TIMEOUT_CONFIG.visualizerPollInterval;
      }
      if (!visualizer.ready) {
        throw new Error('Visualizer failed to initialize within timeout');
      }
      console.log(`[Export] Visualizer ready after ${waitTime}ms`);
    } else {
      console.log('[Export] Visualizer already ready');
    }

    // Log render target details
    const rendererInfo = visualizer.renderer;
    const pixelRatio = rendererInfo?.getPixelRatio ? rendererInfo.getPixelRatio() : 'unknown';
    console.log(`[Export] Render target: ${canvas.width}x${canvas.height}, pixelRatio=${pixelRatio}`);

    return { visualizer, VisualizerClass };
  }

  /**
   * Prepare playlist items and ensure durations
   * @param {Array|File} playlistOrFile - Playlist or single file
   * @param {AudioAnalyzer} analyzer - Audio analyzer instance
   * @returns {Promise<{items: Array, firstBuffer: AudioBuffer, outputFilename: string}>}
   */
  async preparePlaylist(playlistOrFile, analyzer) {
    let includedItems = [];
    let outputFilename = '';
    let firstBuffer = null;

    if (Array.isArray(playlistOrFile)) {
      // Playlist mode
      includedItems = playlistOrFile.filter(item => item.included);
      if (includedItems.length === 0) {
        throw new Error('No tracks selected for export');
      }

      outputFilename = includedItems.length === 1
        ? includedItems[0].name
        : `${includedItems[0].name.replace(/\.[^/.]+$/, '')}_playlist`;

      // Ensure durations (sequential decode, keep first buffer)
      // Cache decoded buffers on the item to prevent double-decode:
      // decodeAudioData detaches the ArrayBuffer, so rawAudioData becomes unusable after first decode.
      for (let idx = 0; idx < includedItems.length; idx++) {
        const item = includedItems[idx];
        if (!item.duration || idx === 0) {
          const buffer = item.audioBuffer || await analyzer.loadAudio(resolveTrackAudioSource(item));
          if (!item.audioBuffer) {
            item.audioBuffer = buffer;
          }
          if (!item.duration) {
            item.duration = buffer.duration;
          }
          if (idx === 0) {
            firstBuffer = buffer;
          }
        }
      }
    } else {
      // Single file mode
      firstBuffer = await analyzer.loadAudio(playlistOrFile);
      outputFilename = playlistOrFile.name;
      includedItems = [{
        name: playlistOrFile.name,
        duration: firstBuffer.duration,
        file: playlistOrFile,
        included: true
      }];

      if (this.onTrackChange) {
        this.onTrackChange(playlistOrFile.name, 1, 1);
      }
    }

    return { items: includedItems, firstBuffer, outputFilename };
  }

  /**
   * Set up audio analysis stream
   * @param {Array} items - Playlist items
   * @param {AudioBuffer} firstBuffer - First audio buffer (already decoded)
   * @param {AudioAnalyzer} analyzer - Audio analyzer
   * @param {Function} onTrackAudio - Callback for encoding audio per track
   * @returns {Object} Frequency stream
   */
  setupFrequencyStream(items, firstBuffer, analyzer, onTrackAudio) {
    const supportsStreaming = typeof analyzer.createFrequencyStream === 'function';

    if (supportsStreaming) {
      return analyzer.createFrequencyStream(items, {
        fps: this.fps,
        fftSize: this.analysisFftSize,
        overlapSamples: this.analysisOverlapSamples,
        firstBuffer,
        onTrackAudio,
      });
    }

    // Fallback to legacy in-memory analysis
    console.warn('[Export] Streaming analyzer unavailable; falling back to full-buffer analysis.');
    return this.createLegacyFrequencyStream(items, analyzer);
  }

  /**
   * Legacy fallback for frequency analysis
   */
  async createLegacyFrequencyStream(items, analyzer) {
    const audioBuffers = [];
    for (const item of items) {
      const buffer = item.audioBuffer || await analyzer.loadAudio(resolveTrackAudioSource(item));
      audioBuffers.push(buffer);
      if (!item.duration) item.duration = buffer.duration;
    }

    const audioBuffer = analyzer.concatenateAudioBuffers(audioBuffers);
    const analysisResult = await analyzer.extractFrequencyDataDirect(audioBuffer);
    const { frequencyData, frameCount } = analysisResult;

    const self = this;
    return {
      frames: (async function* () {
        for (let i = 0; i < frequencyData.length; i++) {
          yield { data: frequencyData[i], frameTime: i / self.fps, trackIndex: 0 };
        }
      })(),
      ready: Promise.resolve({
        sampleRate: audioBuffer.sampleRate,
        numberOfChannels: audioBuffer.numberOfChannels
      }),
      estimatedFrameCount: frameCount,
      legacyAudioBuffer: audioBuffer, // For audio encoding
    };
  }

  /**
   * Export video
   * @param {Array|File} playlistOrFile - Array of playlist items or single audio file
   * @param {Object} options - Export options
   * @param {FileSystemWritableFileStream} options.writableStream - Pre-opened stream for FSA mode
   * @param {string} options.fileName - File name for FSA mode
   * @returns {Promise<Blob|null>} - MP4 video blob (null in FSA streaming mode)
   */
  async export(playlistOrFile, options = {}) {
    // Check WebCodecs support (VideoEncoder and VideoFrame are both required)
    if (!('VideoEncoder' in window) || !('VideoFrame' in window)) {
      throw new Error('WebCodecs not supported in this browser. Use Chrome 94+ or Edge 94+.');
    }

    // Create export-scoped resource registry for tracking WebCodecs resources
    const exportRegistry = new ResourceRegistry(`export-${Date.now()}`);
    ResourceAuditor.registerRegistry(exportRegistry);

    // Pre-export audit to check for resource leaks from previous operations
    const preAudit = ResourceAuditor.auditPreExport();
    if (!preAudit.clean) {
      console.warn('[Export] Pre-export audit FAILED - lingering resources detected:', preAudit);
      ResourceAuditor.dumpAll();
    } else {
      console.log('[Export] Pre-export audit PASSED - no lingering resources (%d registries checked)', preAudit.registriesCount);
    }

    // Load mp4-muxer
    await this.loadMuxer();

    // Streaming mode setup
    const useStreaming = options.writableStream != null;
    this.writableStream = options.writableStream || null;
    this.totalBytesWritten = 0;

    // Progress helper
    let progressValue = 0;
    const updateProgress = (p) => {
      if (!this.onProgress) return;
      const clamped = Math.max(0, Math.min(1, p));
      if (clamped > progressValue) {
        progressValue = clamped;
        this.onProgress(progressValue);
      }
    };
    updateProgress(0);

    // Create analyzer
    const analyzer = new AudioAnalyzer({
      fftSize: this.analysisFftSize,
      fps: this.fps,
      onProgress: (p) => updateProgress(p * ANALYSIS_CONFIG.progressPhases.analysis.end),
    });

    // Prepare playlist and get audio info
    const { items, firstBuffer, outputFilename } = await this.preparePlaylist(playlistOrFile, analyzer);

    // Create render canvas
    const canvas = new OffscreenCanvas(this.width, this.height);

    // State for encoder setup and resources (declared before try for finally access)
    const inMemoryChunks = [];
    const writeQueue = [];
    let visualizer = null;
    let compositor = null;
    let audioEncoder = null;
    let audioEncoderConfigured = false;
    let audioEncoderError = null;
    let videoEncoderError = null;
    let muxer = null;
    let videoEncoder = null;
    let finalized = false;

    try {
    // Initialize visualizer
    const { visualizer: viz, VisualizerClass } = await this.initVisualizer(canvas);
    visualizer = viz;
    const contextType = VisualizerClass.contextType || '2d';

    // Create lyrics compositor if enabled
    compositor = createLyricsCompositor({
      lyricsRenderer: this.lyricsRenderer,
      lyricsEnabled: this.lyricsEnabled,
      width: this.width,
      height: this.height,
      items,
      contextType,
    });

    // Track audio info from first buffer
    const sampleRate = firstBuffer.sampleRate;
    const numberOfChannels = firstBuffer.numberOfChannels;

    // Configure audio encoder lazily (when first track arrives)
    const configureAudioEncoder = (sr, channels) => {
      if (audioEncoderConfigured) return;
      audioEncoder = createAudioEncoder({
        sampleRate: sr,
        numberOfChannels: channels,
        bitrate: this.audioBitrate,
        onOutput: (chunk, meta) => muxer.addAudioChunk(chunk, meta),
        onError: (e) => {
          console.error('[Export] Audio encoder error:', e);
          audioEncoderError = e;
        },
      }, exportRegistry);
      audioEncoderConfigured = true;
    };

    // Set up frequency stream with audio encoding callback
    const freqStream = this.setupFrequencyStream(items, firstBuffer, analyzer, async (buffer, timeOffsetSec, trackIndex) => {
      // Configure encoder on first track
      if (!audioEncoderConfigured) {
        configureAudioEncoder(buffer.sampleRate, buffer.numberOfChannels);
      }

      // Check for prior encoding error
      if (audioEncoderError) {
        throw new Error(`Audio encoding failed on a previous track: ${audioEncoderError.message}`);
      }

      const approxMb = ((buffer.length * buffer.numberOfChannels * 4) / (1024 * 1024)).toFixed(1);
      console.log(`[Audio] Encoding track ${trackIndex + 1}/${items.length} | duration=${buffer.duration.toFixed(2)}s | samples=${buffer.length} | est ~${approxMb} MB`);

      const audioData = audioBufferToAudioData(buffer, Math.round(timeOffsetSec * 1_000_000));
      audioEncoder.encode(audioData);
      audioData.close();

      // Flush encoder to fully process this track before moving to the next.
      // Without this, the encoder's internal queue grows unbounded across tracks,
      // eventually causing an internal error that closes the codec.
      await audioEncoder.flush();
    });

    // Wait for stream ready
    const readyInfo = await freqStream.ready;

    // Select video codec
    const { codec, name: codecName, hardwareSupported } = await selectVideoCodec({
      width: this.width,
      height: this.height,
      bitrate: this.videoBitrate,
      fps: this.fps,
      preset: this.videoCodecPreset,
    });

    // Check for software encoder fallback
    if (!hardwareSupported) {
      const isSoftwareFallback = await detectSoftwareFallback(codec, {
        width: this.width,
        height: this.height,
        bitrate: this.videoBitrate,
        fps: this.fps,
      });
      if (isSoftwareFallback) {
        console.warn('[Export] Software encoding detected - export may be slower. Consider reducing resolution or closing other GPU applications.');
      }
    }

    // Counter for tracking encoded frames (shared with FrameRenderer for stats)
    const encodedFrameCounter = { count: 0 };

    // Create muxer
    muxer = createMuxer({
      width: this.width,
      height: this.height,
      sampleRate: readyInfo.sampleRate,
      numberOfChannels: readyInfo.numberOfChannels,
      streaming: useStreaming,
      writableStream: this.writableStream,
      onBytesWritten: (bytes) => {
        this.totalBytesWritten = bytes;
        if (this.onBytesWritten) this.onBytesWritten(bytes);
      },
      chunks: inMemoryChunks,
      writeQueue,
    });

    // Handle legacy mode audio encoding (must happen after muxer creation
    // because the audio encoder's onOutput callback references muxer)
    if (freqStream.legacyAudioBuffer) {
      configureAudioEncoder(readyInfo.sampleRate, readyInfo.numberOfChannels);
      const audioData = audioBufferToAudioData(freqStream.legacyAudioBuffer, 0);
      audioEncoder.encode(audioData);
      audioData.close();
    }

    // Create video encoder
    videoEncoder = await createVideoEncoder({
      width: this.width,
      height: this.height,
      bitrate: this.videoBitrate,
      fps: this.fps,
      codec,
      onOutput: (chunk, meta) => {
        muxer.addVideoChunk(chunk, meta);
        encodedFrameCounter.count++; // Track encoded frames for stats
      },
      onError: (e) => {
        console.error('[Export] Video encoder error:', e);
        videoEncoderError = e;
      },
    }, exportRegistry);

    // Build track timeline
    const trackTimeline = buildTrackTimeline(items);
    const estimatedFrameCount = freqStream.estimatedFrameCount ||
      Math.ceil(items.reduce((sum, i) => sum + (i.duration || 0), 0) * this.fps);

    // Create frame renderer
    const frameRenderer = new FrameRenderer({
      visualizer,
      videoEncoder,
      fps: this.fps,
      contextType,
      useStreaming,
      writeQueue,
      onProgress: updateProgress,
      onTrackChange: this.onTrackChange,
      onStatsUpdate: this.onStatsUpdate,
      isAborted: () => this.abortRequested,
      getEncoderError: () => videoEncoderError || audioEncoderError,
      encodedFrameCounter, // Share counter for accurate enc fps stats
      resourceRegistry: exportRegistry, // For tracking VideoFrames
    });

    // Render all frames
    const { frameCount, aborted } = await frameRenderer.renderAllFrames(freqStream, {
      canvas,
      compositor,
      trackTimeline,
      estimatedFrameCount,
    });

    // Finalize (handles its own cleanup on success and abort paths)
    const result = await this.finalize({
      aborted,
      muxer,
      videoEncoder,
      audioEncoder,
      visualizer,
      compositor,
      useStreaming,
      writeQueue,
      inMemoryChunks,
      outputFilename,
      updateProgress,
      exportRegistry,
    });
    finalized = true;
    return result;

    } finally {
      // Emergency cleanup for resources not cleaned up by finalize()
      // (e.g., when an error is thrown before reaching finalize)
      if (!finalized) {
        console.warn('[Export] Emergency cleanup - export did not reach finalize()');

        if (visualizer?.dispose) {
          try { visualizer.dispose(); } catch (e) { /* ignore */ }
        }
        if (compositor?.dispose) {
          try { compositor.dispose(); } catch (e) { /* ignore */ }
        }
        if (audioEncoder) {
          try { audioEncoder.close(); } catch (e) { /* ignore */ }
        }
        if (videoEncoder) {
          try { videoEncoder.close(); } catch (e) { /* ignore */ }
        }

        ResourceAuditor.unregisterRegistry(exportRegistry);
      }
    }
  }

  /**
   * Finalize export - flush encoders, clean up, and return result
   */
  async finalize(ctx) {
    const {
      aborted,
      muxer,
      videoEncoder,
      audioEncoder,
      visualizer,
      compositor,
      useStreaming,
      writeQueue,
      inMemoryChunks,
      outputFilename,
      updateProgress,
      exportRegistry,
    } = ctx;

    // Handle abort
    if (aborted) {
      return this.handleAbort(ctx);
    }

    // Log resource state before cleanup
    console.log('[Export] Resource state before cleanup:', exportRegistry.report());

    // Flush audio encoder
    if (audioEncoder) {
      console.log('[Export] Flushing audio encoder');
      await audioEncoder.flush();
      audioEncoder.close();
      exportRegistry.untrack(audioEncoder);
      console.log('[Export] Audio encoder closed');
    }

    // Flush video encoder
    console.log('[Export] Flushing video encoder');
    await videoEncoder.flush();
    videoEncoder.close();
    exportRegistry.untrack(videoEncoder);
    console.log('[Export] Video encoder closed');

    // Clean up visualizer
    console.log('[Export] Disposing visualizer');
    if (visualizer.dispose) {
      visualizer.dispose();
    }

    // Clean up compositor
    if (compositor) {
      compositor.dispose();
    }

    // Wait for hardware resources to release
    const cleanupDelay = getPlatformCleanupDelay();
    console.log(`[Export] Waiting ${cleanupDelay}ms for hardware encoder resources to release...`);
    await new Promise(resolve => setTimeout(resolve, cleanupDelay));

    // Finalize muxer
    console.log('[Export] Finalizing muxer');
    muxer.finalize();
    console.log('[Export] Muxer finalized');

    // Verify resource cleanup
    const verified = exportRegistry.verify();
    if (!verified) {
      console.warn('[Export] Post-export verification FAILED - resource leak detected');
      exportRegistry.dumpTop({ limit: 20 });
    } else {
      console.log('[Export] Post-export verification PASSED - all resources disposed');
    }

    // Unregister from global auditor
    ResourceAuditor.unregisterRegistry(exportRegistry);

    updateProgress(0.95);

    if (useStreaming) {
      // Wait for all queued writes
      console.log(`[Export] Final writeQueue flush, length=${writeQueue.length}`);
      await Promise.all(writeQueue);
      console.log('[Export] Closing stream');
      await this.writableStream.close();
      console.log('[Export] Stream closed');
      this.writableStream = null;
      updateProgress(1);
      return null;
    } else {
      // Assemble in-memory chunks
      console.log(`[Export] Assembling ${inMemoryChunks.length} chunks`);
      const blob = assembleChunksToBlob(inMemoryChunks);
      inMemoryChunks.length = 0;

      // Trigger download
      this.downloadBlob(blob, this.getOutputFilename(outputFilename));
      updateProgress(1);
      return blob;
    }
  }

  /**
   * Handle export abort - clean up resources
   */
  async handleAbort(ctx) {
    const {
      videoEncoder,
      audioEncoder,
      visualizer,
      compositor,
      useStreaming,
      inMemoryChunks,
      exportRegistry,
    } = ctx;

    console.log('[Export] Cleaning up after abort...');

    // Flush and close video encoder
    try {
      console.log('[Export] Flushing video encoder (abort)');
      await videoEncoder.flush();
    } catch (e) {
      console.warn('[Export] Error flushing video encoder during abort:', e);
    }
    videoEncoder.close();
    if (exportRegistry) exportRegistry.untrack(videoEncoder);
    console.log('[Export] Video encoder closed (abort)');

    // Close audio encoder if initialized
    if (audioEncoder) {
      audioEncoder.close();
      if (exportRegistry) exportRegistry.untrack(audioEncoder);
    }

    // Dispose visualizer
    console.log('[Export] Disposing visualizer (abort)');
    if (visualizer.dispose) {
      visualizer.dispose();
    }

    // Dispose compositor
    if (compositor) {
      compositor.dispose();
    }

    // Abort stream if streaming
    if (useStreaming && this.writableStream) {
      try {
        await this.writableStream.abort();
      } catch (e) {
        console.warn('[Export] Error aborting stream:', e);
      }
      this.writableStream = null;
    }

    // Clear in-memory chunks
    inMemoryChunks.length = 0;

    // Wait for hardware resources
    const cleanupDelay = TIMEOUT_CONFIG.hardwareRelease;
    console.log(`[Export] Waiting ${cleanupDelay}ms for hardware encoder resources to release...`);
    await new Promise(resolve => setTimeout(resolve, cleanupDelay));

    // Verify and clean up registry
    if (exportRegistry) {
      const verified = exportRegistry.verify();
      if (!verified) {
        console.warn('[Export] Post-abort verification FAILED - resource leak detected');
        exportRegistry.dumpTop({ limit: 20 });
      } else {
        console.log('[Export] Post-abort verification PASSED - all resources disposed');
      }
      ResourceAuditor.unregisterRegistry(exportRegistry);
    }

    this.abortRequested = false;
    console.log('[Export] Abort cleanup complete');
    return null;
  }

  /**
   * Generate output filename
   */
  getOutputFilename(inputName) {
    const baseName = inputName.replace(/\.[^/.]+$/, '');
    return `${baseName}_visualized.mp4`;
  }

  /**
   * Trigger file download
   */
  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// ES Module export
export { VideoExporter };
